import React, { useEffect, useState, useCallback } from "react";
import { useMediaQuery } from "react-responsive";
import PropTypes from "prop-types";
import { Box, Grid } from "@mui/material";
import Card from "../../components/Cards/Card";
import NativeObject from "../../components/NativeObject/NativeObject";
import { useSelections } from "../../context/Selections";
import LayoutWrapper from "../../components/LayoutWrapper/LayoutWrapper";
import OverviewCard from "../../components/OverviewCard/OverviewCard";
import ComboHeader from "../../components/ComboHeader/ComboHeader";
import Kpi from "../../components/KPI/Kpi";
import ProgressBar from "../../components/ProgressBar/ProgressBar";

function OverviewDesktop({ app, config }) {
  const notFullHd = useMediaQuery({ query: "(max-width: 1536px)" });

  const { selections } = useSelections();
  const [scorePercentageNum, setScorePercentageNum] = useState(0);
  const [scorePercentageColor, setScorePercentageColor] = useState("#38BC9A");
  const [extraScorePercentageNum, setExtraScorePercentageNum] = useState(0);
  const [extraScorePercentageColor, setExtraScorePercentageColor] =
    useState("#38BC9A");
  const selectedQuarter = Kpi({
    app,
    objectId: "jzfBCy",
    updatesWithSelection: true,
  });
  const [score, totalScore, scorePercentage] = [
    Kpi({
      app,
      objectId: "kvsLs",
      updatesWithSelection: true,
    }),
    Kpi({
      app,
      objectId: "hhqJDa",
      updatesWithSelection: true,
    }),
    Kpi({
      app,
      objectId: "MFYzdU",
      updatesWithSelection: true,
    }),
  ];

  const [extraScore, extraTotalScore, extraScorePercentage] = [
    Kpi({
      app,
      objectId: "PGBjwz",
      updatesWithSelection: true,
    }),
    Kpi({
      app,
      objectId: "aMVKK",
      updatesWithSelection: true,
    }),
    Kpi({
      app,
      objectId: "Xwzxh",
      updatesWithSelection: true,
    }),
  ];

  const [selectedGroup, setSelectedGroup] = useState("");
  const [singleGroupView, setSingleGroupView] = useState(false);
  const [textQuarter, setTextQuarter] = useState();

  const getColorFromPercentage = (percentage) => {
    switch (true) {
      case percentage < 85:
        return "#E95248";
      case percentage < 100:
        return "#FDB949";
      default:
        return "#38BC9A";
    }
  };

  const fetchKPIValue = useCallback(
    async (objectId, position) => {
      if (app && objectId) {
        let id = objectId;
        await app.visualization.get(id).then((res) => {
          const value =
            res?.model?.layout?.qHyperCube?.qGrandTotalRow[0]?.qText ??
            res?.model?.layout?.qHyperCube?.qGrandTotalRow[0]?.qNum ??
            null;

          setTextQuarter(value && value[0] !== "-" ? value : null);
        });
      }
    },
    [app, setTextQuarter]
  );

  useEffect(() => {
    if (app) {
      // update title with selected distributor (when only 1 is selected)
      const distributors = selections.length
        ? selections.find((s) => s.qField === "Grupo Distribuição")
        : null;
      setSelectedGroup(
        distributors
          ? distributors?.qSelectedCount === 1
            ? `: ${distributors.qSelected}`
            : ""
          : ""
      );
      setSingleGroupView(distributors?.qSelectedCount === 1);
      fetchKPIValue("jzfBCy");
    }
  }, [app, selections]);

  useEffect(() => {
    const num = scorePercentage ? parseFloat(scorePercentage) : 0;
    setScorePercentageNum(num > 100 ? 100 : num);
  }, [scorePercentage]);

  useEffect(() => {
    setScorePercentageColor(getColorFromPercentage(scorePercentageNum));
  }, [scorePercentageNum]);

  useEffect(() => {
    const num = extraScorePercentage ? parseFloat(extraScorePercentage) : 0;
    setExtraScorePercentageNum(num > 100 ? 100 : num);
  }, [extraScorePercentage]);

  useEffect(() => {
    setExtraScorePercentageColor(
      getColorFromPercentage(extraScorePercentageNum)
    );
  }, [extraScorePercentageNum]);

  return (
    <div className="app overview">
      <div className="appContent">
        <LayoutWrapper>
          <Grid
            container
            flexDirection={"column"}
            justifyContent={"flex-start"}
            rowGap={2}
          >
            {/* TITLE */}
            <Grid
              item
              xs
              sx={{ display: "flex", alignItems: "center", gap: "10px" }}
            >
              <Box component={"h1"}>{`Overview`}</Box> {"-"}
              <Grid
                className="container-periodo"
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
                xs
                item
              >
                <p>Período Analisado:</p>

                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "17px",
                    color: "black",
                  }}
                >
                  {textQuarter}
                </p>
              </Grid>
            </Grid>

            {/* KPI CARDS CONTAINER */}
            <Grid
              container
              item
              xs
              flexDirection={"column"}
              justifyContent={"flex-start"}
              alignItems={"flex-start"}
              rowGap={2}
            >
              <Grid
                container
                item
                flexDirection={"row"}
                justifyContent={"flex-start"}
                alignItems={"flex-start"}
                columnGap={2}
                xs
              >
                <Grid
                  container
                  item
                  flexDirection={"row"}
                  justifyContent={"flex-start"}
                  alignItems={"flex-start"}
                  columnGap={2}
                  xs
                >
                  {/* AREA ACTING CARD */}
                  <Grid
                    item
                    xs
                    sx={{
                      width: "100%",
                    }}
                  >
                    <OverviewCard
                      app={app}
                      cardTitle={"Área de Atuação"}
                      firstKpiTitle={"Sell out - Meta 1"}
                      secondKpiTitle={"Positivação - Meta 2"}
                      firstKpiInfo={{
                        firstGroup: {
                          title: "Cuidados com a pele",
                          valueId: "d8c7cc13-68fc-440f-9ac5-76a56b65da27",
                          goalValueId: "54f883a5-45cc-4779-9ecb-2158364ab882",
                          percentageId: "d84ad771-4084-4ae2-9110-9b1804154950",
                          scoreId: "fpKLPCD",
                        },
                        secondGroup: {
                          title: "Esmaltes",
                          goalValueId: "pPXFsYV",
                          valueId: "PGEJMrx",
                          percentageId: "KUbxsW",
                          scoreId: "KNNqmB",
                        },
                        thirdGroup: {
                          title: "Cuidados pessoais",
                          goalValueId: "ZwfPEvV",
                          valueId: "umzcdD",
                          percentageId: "qeemjRK",
                          scoreId: "VdPJh",
                        },
                      }}
                      secondKpiInfo={{
                        firstGroup: {
                          title: "Cuidados com a pele",
                          goalValueId: "52159a02-9be4-4a8d-90a0-eb09e73ed73e",
                          valueId: "fd51dc92-74aa-47b0-84a7-1e548366c814",
                          percentageId: "090cdf06-2a04-4dc7-a449-580f97c29e79",
                          scoreId: "bWCWtGG",
                        },
                        secondGroup: {
                          title: "Esmaltes",
                          goalValueId: "d3f64578-9784-40f9-a91d-18eb55f1820b",
                          valueId: "a624f6cf-031b-49b3-b5f6-3e2aacc3eaf5",
                          percentageId: "c067a10d-92a1-437d-a0c3-f4dae4366455",
                          scoreId: "dnJDeFr",
                        },
                        thirdGroup: {
                          title: "Cuidados pessoais",
                          goalValueId: "3fec3843-d1f9-458e-87f5-e25be7fa4730",
                          valueId: "4e425d89-1635-49d6-9599-650a6ce72f62",
                          percentageId: "5bd26d4c-e4b2-4e9a-af1f-c24758434f59",
                          scoreId: "CbcURA",
                        },
                      }}
                      singleGroupView={singleGroupView}
                    />
                  </Grid>

                  {/* GOLDEN STORES CARD */}
                  <Grid item xs sx={{ width: "100%" }}>
                    <OverviewCard
                      app={app}
                      accumulated={true}
                      cardTitle={"Golden Stores"}
                      firstKpiTitle={"Sell out - Meta 3"}
                      secondKpiTitle={"Distribuição - meta 4"}
                      firstKpiInfo={{
                        firstGroup: {
                          title: "Cuidados com a pele",
                          goalValueId: "e9c88e5d-4395-4531-a633-b73a5f23fff2",
                          valueId: "c72660a2-b39b-4f51-8d40-8424f61d2d0d",
                          percentageId: "4c809e84-1ecf-43cf-91d0-a82b0c7507c0",
                          scoreId: "DmpMrY",
                        },
                        secondGroup: {
                          title: "Esmaltes",
                          goalValueId: "725a6c10-cd23-406c-afc5-c9123c8cf24e",
                          valueId: "51ebf2be-face-4a19-a7ea-953c1d4837a9",
                          percentageId: "a302f758-ce7e-414b-b498-4da7a173167d",
                          scoreId: "BtcAnhB",
                        },
                        thirdGroup: {
                          title: "Cuidados pessoais",
                          goalValueId: "badae530-6720-48bc-9aa4-f526a7b59f1a",
                          valueId: "d348a92c-2fd1-4b67-92a8-274132fce422",
                          percentageId: "1b5111aa-e061-4b09-a42e-f89eb4666368",
                          scoreId: "fvwAuY",
                        },
                      }}
                      secondKpiInfo={{
                        firstGroup: {
                          title: "PDVs Alimentar",
                          goalValueId: "79bc2230-2df7-42a2-9005-37fc2b36a063",
                          valueId: "00b801aa-c7aa-443a-a1c1-738b4935a42c",
                          percentageId: "9e9098f8-f782-4827-a64e-98bf0d23e501",
                        },
                        secondGroup: {
                          title: "PDVs Farma",
                          goalValueId: "jaQFzdz",
                          valueId: "PMrYR",
                          percentageId: "MhuyAQT",
                          scoreId: "",
                        },
                        thirdGroup: {
                          title: "PDVs Perfumaria",
                          goalValueId: "cFdRTzu",
                          valueId: "QxxbuS",
                          percentageId: "RPkJDX",
                        },
                        accumulated: {
                          title: "Total Acumulado",
                          valueId: "rQYSVR",
                          goalValueId: "CyHPT",
                          percentageId: "XLRVFxH",
                          scoreId: "ZBrzSA",
                        },
                      }}
                      singleGroupView={singleGroupView}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* EXTRA SCORES CONTAINER */}
            <Grid
              container
              item
              flexDirection={"row"}
              justifyContent={"flex-start"}
              columnGap={2}
              xs
            >
              <Grid item key={"Pontuação Extra"} xs>
                <Card
                  app={app}
                  header={{
                    hardTitle: "Pontuação Extra",
                  }}
                  style={{
                    height: "100%",
                  }}
                >
                  {/* SCORE KPI */}
                  <Grid
                    container
                    item
                    flexDirection={"row"}
                    justifyContent={"flex-start"}
                    alignItems={"flex-start"}
                    rowGap={1}
                    sx={{
                      padding: "0px 12px 4px",
                    }}
                  >
                    <Grid
                      className={`value-wrapper`}
                      container
                      item
                      xs={12}
                      flexDirection={"row"}
                      justifyContent={"flex-start"}
                      alignItems={"center"}
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Grid
                        item
                        container
                        xs
                        flexDirection={"row"}
                        justifyContent={"flex-start"}
                        alignItems={"center"}
                        columnGap={1}
                        sx={{
                          marginBottom: "4px",
                        }}
                      >
                        {/* SCORE */}
                        <Box
                          component={"span"}
                          className="value"
                          style={{
                            color: extraScorePercentageColor,
                          }}
                        >
                          {extraScore}
                        </Box>
                      </Grid>
                    </Grid>
                    {/* PROGRESS BAR */}
                    <Grid
                      container
                      item
                      flexDirection={"column"}
                      justifyContent={"flex-start"}
                      alignItems={"flex-start"}
                      style={{
                        width: "100%",
                      }}
                      xs={12}
                    >
                      <ProgressBar
                        key={"Sell out"}
                        bgColor={extraScorePercentageColor}
                        completed={extraScorePercentageNum}
                      />
                    </Grid>

                    {/* GOAL AND PERCENTAGE */}
                    <Grid
                      container
                      item
                      flexDirection={"row"}
                      justifyContent={"flex-start"}
                      alignItems={"flex-start"}
                      xs
                      columnGap={1}
                    >
                      {/* TOTAL SCORE */}
                      <Grid
                        className={`goal-wrapper`}
                        container
                        item
                        flexDirection={"row"}
                        justifyContent={"flex-start"}
                        alignItems={"center"}
                        columnGap={0.8}
                        xs
                      >
                        <Grid
                          item
                          container
                          xs
                          flexDirection={"row"}
                          justifyContent={"flex-start"}
                          alignItems={"center"}
                          columnGap={1}
                        >
                          {/* TOTAL SCORE VALUE */}
                          <Box component={"span"} className="goal-value">
                            {extraTotalScore}
                          </Box>
                          {/* TOTAL SCORE TEXT */}
                          <Box component={"span"} className="goal-text">
                            Meta
                          </Box>
                        </Grid>
                      </Grid>

                      {/* GOAL PERCENTAGE */}
                      <Grid
                        className={`percentage-wrapper`}
                        container
                        item
                        flexDirection={"row"}
                        justifyContent={"flex-start"}
                        alignItems={"center"}
                        columnGap={0.8}
                        xs={notFullHd ? 4 : 6}
                      >
                        <Grid item>
                          <Box
                            component={"span"}
                            className="percentage-value"
                            style={{
                              color: extraScorePercentageColor,
                            }}
                          >
                            {extraScorePercentage}
                          </Box>
                        </Grid>
                        <Grid item>
                          <Box component={"span"} className="percentage-text">
                            Atingimento
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              <Grid item key={"Pontuação Total"} xs>
                <Card
                  app={app}
                  header={{
                    hardTitle: "Pontuação Total",
                  }}
                  style={{
                    height: "100%",
                  }}
                >
                  {/* SCORE KPI */}
                  <Grid
                    container
                    item
                    flexDirection={"row"}
                    justifyContent={"flex-start"}
                    alignItems={"flex-start"}
                    rowGap={1}
                    sx={{
                      padding: "0px 12px 4px",
                    }}
                  >
                    <Grid
                      className={`value-wrapper`}
                      container
                      item
                      xs={12}
                      flexDirection={"row"}
                      justifyContent={"flex-start"}
                      alignItems={"center"}
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Grid
                        item
                        container
                        xs
                        flexDirection={"row"}
                        justifyContent={"flex-start"}
                        alignItems={"center"}
                        columnGap={1}
                        sx={{
                          marginBottom: "4px",
                        }}
                      >
                        {/* SCORE */}
                        <Box
                          component={"span"}
                          className="value"
                          style={{
                            color: scorePercentageColor,
                          }}
                        >
                          {score}
                        </Box>
                        <Box
                          component={"span"}
                          className="value"
                          style={{
                            color: scorePercentageColor,
                          }}
                        >
                          {`pontos realizados`}
                        </Box>
                      </Grid>
                    </Grid>
                    {/* PROGRESS BAR */}
                    <Grid
                      container
                      item
                      flexDirection={"column"}
                      justifyContent={"flex-start"}
                      alignItems={"flex-start"}
                      style={{
                        width: "100%",
                      }}
                      xs={12}
                    >
                      <ProgressBar
                        key={"Sell out"}
                        bgColor={scorePercentageColor}
                        completed={scorePercentageNum}
                      />
                    </Grid>

                    {/* GOAL AND PERCENTAGE */}
                    <Grid
                      container
                      item
                      flexDirection={"row"}
                      justifyContent={"flex-start"}
                      alignItems={"flex-start"}
                      xs
                      columnGap={1}
                    >
                      {/* TOTAL SCORE */}
                      <Grid
                        className={`goal-wrapper`}
                        container
                        item
                        flexDirection={"row"}
                        justifyContent={"flex-start"}
                        alignItems={"center"}
                        columnGap={0.8}
                        xs
                      >
                        <Grid
                          item
                          container
                          xs
                          flexDirection={"row"}
                          justifyContent={"flex-start"}
                          alignItems={"center"}
                          columnGap={1}
                        >
                          {/* TOTAL SCORE VALUE */}
                          <Box component={"span"} className="goal-value">
                            {totalScore}
                          </Box>
                          {/* TOTAL SCORE TEXT */}
                          <Box component={"span"} className="goal-text">
                            pontos possíveis
                          </Box>
                        </Grid>
                      </Grid>

                      {/* GOAL PERCENTAGE */}
                      <Grid
                        className={`percentage-wrapper`}
                        container
                        item
                        flexDirection={"row"}
                        justifyContent={"flex-start"}
                        alignItems={"center"}
                        columnGap={0.8}
                        xs={notFullHd ? 4 : 6}
                      >
                        <Grid item>
                          <Box
                            component={"span"}
                            className="percentage-value"
                            style={{
                              color: scorePercentageColor,
                            }}
                          >
                            {scorePercentage}
                          </Box>
                        </Grid>
                        <Grid item>
                          <Box component={"span"} className="percentage-text">
                            Atingimento
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>

            {/* RANKING CARD */}
            <Grid item xs sx={{ height: "100%" }}>
              <Card
                app={app}
                style={{
                  height: "100%",
                  width: "100%",
                }}
              >
                <Grid
                  container
                  flexDirection={"column"}
                  justifyContent={"space-between"}
                >
                  <Grid item xs sx={{ height: "100%" }}>
                    <ComboHeader
                      reference={{
                        title: {
                          hard: "Ranking Geral de Pontos",
                        },
                      }}
                      legends={[
                        {
                          type: "bar",
                          iconColor: "#fdb949",
                          name: "Pontos realizados",
                        },
                        {
                          type: "bar",
                          iconColor: "#ff7f27",
                          name: "Pontos possíveis",
                        },
                      ]}
                    />
                  </Grid>
                  <Grid
                    item
                    xs
                    sx={{
                      minHeight: notFullHd ? "392px" : "450px",
                      height: "100%",
                      width: "100%",
                    }}
                  >
                    <NativeObject
                      app={app}
                      key="uCRdDB"
                      reference={{ id: "uCRdDB" }}
                      style={{
                        padding: "0 24px ",
                      }}
                    />
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </LayoutWrapper>
      </div>
    </div>
  );
}

OverviewDesktop.defaultProps = {
  app: null,
  config: {},
};

OverviewDesktop.propTypes = {
  app: PropTypes.object,
  config: PropTypes.object,
};

export default OverviewDesktop;
